/**
 * The replay viewer: loads a finished game's record (seed + decks + action
 * log), reconstructs every position by replaying the pure reducer, and lets
 * the viewer step through them. The board and move log are the same
 * components the live game uses, driven by a read-only controller over a
 * position cursor.
 */
import { createMemo, createResource, createSignal, onCleanup, Show } from "solid-js";
import type { GameState, PlayerId } from "../../core";
import { createGameController } from "../../app/controller";
import type { GameBackend } from "../../app/store";
import { fetchReplay } from "../../app/api";
import { replayStates, type ReplayRecord } from "../../shared/replay";
import { PLAYER_ACCENT } from "../../app/config";
import { Board } from "../components/Board";
import { MoveLog } from "../components/MoveLog";
import { DeckTracker } from "../components/DeckTracker";
import { ThemeToggle } from "../components/ThemeToggle";

export function ReplayScreen(props: { gameId: string; onExit: () => void }) {
  const [replay] = createResource(() => props.gameId, fetchReplay);

  return (
    <Show
      when={replay()}
      fallback={
        <div class="app">
          <div class="overlay" style={{ position: "static", background: "transparent", "min-height": "60vh" }}>
            <div class="overlay-card">
              <Show when={replay.error} fallback={<><div class="spinner" /><p style={{ margin: "14px 0 0" }}>Loading game…</p></>}>
                <h2>Game not found</h2>
                <p>{String(replay.error?.message ?? "This replay doesn't exist.")}</p>
                <button class="btn" onClick={props.onExit}>Back</button>
              </Show>
            </div>
          </div>
        </div>
      }
    >
      {(r) => <ReplayViewer replay={r()} onExit={props.onExit} />}
    </Show>
  );
}

function ReplayViewer(props: { replay: ReplayRecord; onExit: () => void }) {
  // Every position, precomputed once (a few hundred small states at most).
  const states = createMemo(() => replayStates(props.replay));
  const last = () => states().length - 1;
  const [index, setIndex] = createSignal(0);

  const goto = (i: number) => setIndex(Math.max(0, Math.min(last(), i)));

  // A read-only backend whose "current state" is the cursor position.
  const backend: GameBackend = {
    get state(): GameState {
      return states()[index()]!;
    },
    error: null,
    canUndo: false,
    dispatch: () => false,
    undo() {},
    clearError() {},
  };
  const controller = createGameController(backend, { mySeat: 0, spectator: true });

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "ArrowLeft") goto(index() - 1);
    if (e.key === "ArrowRight") goto(index() + 1);
    if (e.key === "Home") goto(0);
    if (e.key === "End") goto(last());
  };
  window.addEventListener("keydown", onKey);
  onCleanup(() => window.removeEventListener("keydown", onKey));

  const result = () => {
    const w = props.replay.winner;
    if (w === null) return "Draw";
    return `${props.replay.players[w].name} won`;
  };

  const name = (seat: PlayerId) => props.replay.players[seat].name;

  return (
    <div class="app app--wide">
      <div class="topbar">
        <div class="brand">
          <h1>Chessdeck</h1>
          <span class="tag">
            replay · <span style={{ color: PLAYER_ACCENT[0] }}>{name(0)}</span> vs{" "}
            <span style={{ color: PLAYER_ACCENT[1] }}>{name(1)}</span> · {result()}
            {props.replay.ranked ? " · ranked" : ""}
          </span>
        </div>
        <div class="topbar-actions">
          <ThemeToggle />
          <button class="btn btn-ghost" onClick={props.onExit}>
            Close
          </button>
        </div>
      </div>

      <div class="game">
        <div class="col">
          <div class="panel">
            <h2>Position</h2>
            <div class="field-row" style={{ gap: "6px", "flex-wrap": "wrap" }}>
              <button class="btn" onClick={() => goto(0)} disabled={index() === 0} title="Start (Home)">
                ⏮
              </button>
              <button class="btn" onClick={() => goto(index() - 1)} disabled={index() === 0} title="Back (←)">
                ◀
              </button>
              <button class="btn" onClick={() => goto(index() + 1)} disabled={index() === last()} title="Forward (→)">
                ▶
              </button>
              <button class="btn" onClick={() => goto(last())} disabled={index() === last()} title="End (End)">
                ⏭
              </button>
            </div>
            <input
              type="range"
              min="0"
              max={last()}
              value={index()}
              onInput={(e) => goto(Number(e.currentTarget.value))}
              style={{ width: "100%", "margin-top": "10px" }}
            />
            <p style={{ margin: "8px 0 0", opacity: 0.75 }}>
              Ply {index()} / {last()}
            </p>
          </div>
          <div class="panel">
            <h2>Decks &amp; losses</h2>
            <DeckTracker controller={controller} />
          </div>
        </div>

        <Board controller={controller} />

        <div class="col">
          <div class="panel">
            <h2>Move log</h2>
            <MoveLog controller={controller} />
          </div>
        </div>
      </div>
    </div>
  );
}
